import { LearnerPageChrome } from "@/pages/learners/page-chrome"
import { Provider } from "react-redux"
import { ShowPageProps as ShowPageProps } from "./page-props"
import {
  createPageStore,
  loadTimetables,
  PreferenceId,
  ClassPriorityId,
  selectCalendar,
  selectPreference,
  selectPreferenceClass,
  selectPreferenceIds,
  selectSubject,
  selectSubjectIds,
  selectTimetableSession,
  PageState,
  SubjectId,
  selectFullCalendarEvents,
  selectGetSubjectColours,
  usePageSelector,
  selectSubjectPreferences,
  PreferenceEntity,
  selectPreferenceClassPriorities,
  usePageDispatch,
  preferenceHovered,
  priorityHovered,
  ClassPriorityEntity,
  SubjectEntity,
  selectPreferencePriority,
  PageReduxContext,
} from "@/pages/learners/preferences/show-reducer"
import { useFullCalendarApi } from "@/lib/fullCalendar"
import { useTranslation } from "react-i18next"
import {
  calculateEventStartCalendarTimezoneCoerced,
  displayDay,
  displayPlainTime,
} from "@/lib/timetableZonedDatetime"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button-variants"
import { PageBody } from "@/components/ui/layout"
import { CircleCheck, Info } from "lucide-react"
import { Colours, GetColours } from "@/constants/priorityColours"
import { useGetColours } from "@/lib/useGetColours"
import { Button } from "@/components/ui/button"
import { useEffect, useId, useState } from "react"
import TransactionDialog, {
  TransactionDialogTrigger,
} from "@/components/shared/transaction-dialog"
import { Badge } from "@/components/ui/badge"
import {
  CalendarCard,
  CalendarSidebarLayout,
} from "@/components/shared/calendar-layout"
import { PreferenceCalendar } from "@/components/learners/preferences/preference-calendar"
import { ClassPreferenceCard } from "@/components/learners/preferences/class-preference-card"
import { ROUTES } from "@/constants"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Label } from "@/components/ui/label"
import { Link, usePathParams, useProps } from "@/lib/page-rendering"

// A student's preferences: the student's own page
// (students/:student_id/preferences?school_id=) and, re-exported, the school
// admin's (schools/:school_id/students/:student_id/preferences). The chrome
// finds each one's trail by its component name.
export default function ShowWithStore() {
  const { preferences, ...rest } = useProps<ShowPageProps>()
  const [store] = useState(createPageStore())
  useEffect(() => {
    store.dispatch(loadTimetables({ subjectsPayload: preferences }))
  }, [store, preferences])
  return (
    <Provider store={store} context={PageReduxContext}>
      <Show {...rest} />
    </Provider>
  )
}

function Show({ student }: { student: ShowPageProps["student"] }) {
  const [calendarRef] = useFullCalendarApi("StudentPreferences")

  const { cycleLength } = usePageSelector(selectCalendar)
  const getColours = useGetColours()

  const { t } = useTranslation(["preferences"])
  const events = usePageSelector((state) =>
    selectFullCalendarEvents(state, { tFunction: t, getColours }),
  )
  const { calendarTimezoneId, startTemporalInstant } = usePageSelector(
    (state) => selectCalendar(state),
  )

  const preferencesById = usePageSelector(selectPreferenceIds)
  const subjectIds = usePageSelector(selectSubjectIds)
  return (
    <>
      <LearnerPageChrome
        records={{ "learners/school_students/show": student.label }}
      />
      <PageBody>
        <CalendarSidebarLayout>
          <ClassPreferenceCard>
            <ul className="flex flex-col gap-4 pt-4">
              {subjectIds.map((subjectId) => (
                <SubjectPreference
                  key={subjectId}
                  subjectId={subjectId}
                  getColours={getColours}
                />
              ))}
            </ul>
          </ClassPreferenceCard>
          <CalendarCard>
            <PreferenceCalendar
              cycleLength={cycleLength}
              calendarRef={calendarRef}
              events={events}
              calendarStartInstant={startTemporalInstant}
              calendarTimezoneId={calendarTimezoneId}
              eventContent={({
                event: {
                  title,
                  extendedProps: {
                    preferenceId,
                    priorityId,
                    timetableSessionIndex,
                  },
                },
              }) => {
                if (!preferencesById.has(preferenceId)) return null
                return (
                  <Event
                    title={title}
                    preferenceId={preferenceId}
                    priorityId={priorityId}
                    timetableSessionIndex={timetableSessionIndex}
                  />
                )
              }}
            />
          </CalendarCard>
        </CalendarSidebarLayout>
      </PageBody>
    </>
  )
}

// The school is in the path on the school admin's page and in the query on
// the student's own.
function useSchoolId() {
  return (
    usePathParams().schoolId ??
    new URL(window.location.href).searchParams.get("school_id")!
  )
}

function SubjectPreference({
  subjectId,
  getColours,
}: {
  subjectId: SubjectId
  getColours: GetColours
}) {
  const { name, code, tamsSchoolInput, hasUpgradeRequest, canRequestUpgrade } =
    usePageSelector((state: PageState) => selectSubject(state, { subjectId }))
  const subjectPreferences = usePageSelector((state) =>
    selectSubjectPreferences(state, { subjectId }),
  )
  const getSubjectColours = usePageSelector((state) =>
    selectGetSubjectColours(state, { getColours }),
  )
  const { textColour, bgColour } = getSubjectColours(0, subjectId)

  const { t } = useTranslation(["common", "preferences"])
  const schoolId = useSchoolId()

  const labelId = useId()
  return (
    <li aria-labelledby={labelId}>
      <div className="grid grid-cols-[2fr_1fr] items-center justify-between gap-2">
        <span
          className={`flex h-fit w-fit rounded px-2 py-1 text-sm ${bgColour} ${textColour}`}
          id={labelId}
        >
          {name} - ({code})
        </span>
        <div className="text-right">
          <TransactionDialog
            route="./preference_upgrade_request"
            options={{
              method: "post",
              data: {
                school_id: schoolId,
                subject_id: subjectId,
              },
            }}
            initDescription={t("preferences:show.actions.upgrade_description")}
            pendingDescription={t("preferences:show.actions.upgrade_pending")}
            successDescription={t("preferences:show.actions.upgrade_success")}
            errorDescription={t("preferences:show.actions.upgrade_error")}
          >
            {tamsSchoolInput ? (
              hasUpgradeRequest ? (
                <Badge
                  className="bg-brand-light-wise-green text-left whitespace-normal [&>svg]:size-6"
                  data-testid="upgrade-request-badge"
                >
                  <CircleCheck /> Upgrade Requested
                </Badge>
              ) : (
                <TransactionDialogTrigger>
                  <Button
                    disabled={!canRequestUpgrade}
                    className="h-fit whitespace-normal"
                  >
                    {t("preferences:show.actions.upgrade")}
                  </Button>
                </TransactionDialogTrigger>
              )
            ) : null}
          </TransactionDialog>
        </div>
      </div>
      <ol className="flex flex-col gap-4 pt-4">
        {subjectPreferences.map((preference) => (
          <ClassPreference
            key={preference.id}
            preference={preference}
            subjectId={subjectId}
            colours={getSubjectColours(preference.rank, subjectId)}
          />
        ))}
      </ol>
    </li>
  )
}

function Event({
  title,
  preferenceId,
  priorityId,
  timetableSessionIndex,
}: {
  title: string
  preferenceId: PreferenceId
  priorityId: ClassPriorityId
  timetableSessionIndex: number
}) {
  const { calendarTimezoneId, startTemporalZonedDateTime } =
    usePageSelector(selectCalendar)
  const {
    teacherName,
    listingProfileName,
    name,
    subjectId,
    timezoneId: eventTimezoneId,
  } = usePageSelector((state: PageState) =>
    selectPreferenceClass(state, {
      preference: selectPreference(state, { preferenceId }),
    }),
  )

  const { name: subjectName } = usePageSelector((state: PageState) =>
    selectSubject(state, { subjectId }),
  )
  const { day, startTime, duration } = usePageSelector((state: PageState) =>
    selectTimetableSession(state, { priorityId, timetableSessionIndex }),
  )
  const educatorName = teacherName
    ? `${teacherName} (${listingProfileName})`
    : listingProfileName
  const startZonedDateTime = calculateEventStartCalendarTimezoneCoerced({
    startTime,
    day,
    calendarTimezoneId,
    eventTimezoneId,
    startTemporalZonedDateTime,
  })
  const { t } = useTranslation("preferences")

  return (
    <div className="fc-event-main-frame h-full">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className={cn(
              buttonVariants({ variant: "link" }),
              "fc-event-title fc-stick top-0 m-0 h-min w-full justify-start p-0 text-xs text-current ring-current!",
            )}
          >
            {title}
          </TooltipTrigger>
          <TooltipContent className="text-white">
            {t("timetable.schedule.educator_name", { name: educatorName })}
            <br />
            {t("timetable.schedule.custom_class_name", { name })}
            <br />
            {t("timetable.schedule.subject", { name: subjectName })}
            <br />
            {t("timetable.schedule.duration", { duration })}
            <br />
            {t("timetable.schedule.fixed", {
              day: displayDay(day),
              start: displayPlainTime(startZonedDateTime.toPlainTime()),
              end: displayPlainTime(
                startZonedDateTime.add({ minutes: duration }).toPlainTime(),
              ),
            })}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}

function ClassPreference({
  preference,
  subjectId,
  colours,
}: {
  preference: PreferenceEntity
  subjectId: SubjectId
  colours: Colours
}) {
  const { t } = useTranslation(["preferences", "common"])
  const { rank } = preference
  const preferenceClassPriorities = usePageSelector((state) =>
    selectPreferenceClassPriorities(state, { preference }),
  )

  const classDetails = usePageSelector((state) =>
    selectPreferenceClass(state, { preference }),
  )

  const { cardBorderColour, textColour, bgColour } = colours
  const subject = usePageSelector((state) =>
    selectSubject(state, { subjectId }),
  )

  const dispatch = usePageDispatch()

  return (
    <li
      className={`flex flex-col gap-2 rounded-md border border-l-4 border-gray-200 p-4 shadow-sm ${cardBorderColour}`}
      aria-label={t("show.class.preference.label", { rank })}
      onPointerEnter={() => dispatch(preferenceHovered(preference))}
      onPointerLeave={() => dispatch(preferenceHovered(null))}
    >
      <div className="grid grid-cols-2 items-center">
        <span
          className={`flex h-fit w-fit rounded px-2 py-1 text-xs ${bgColour} ${textColour}`}
        >
          {t("edit.class.preference.label", { rank })}
        </span>
        <Button
          asChild
          variant={"link"}
          className="col-span-2 h-fit px-0 text-sm whitespace-normal"
        >
          <Link href={ROUTES.SEARCH_CLASSES_RESULT_PATH(classDetails.uuid)}>
            {t("edit.class.label", {
              className: classDetails.name,
              classListingName: classDetails.listingProfileName,
            })}
          </Link>
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <span>{t("edit.class.schedule.label")}</span>
      </div>
      <Accordion type="multiple">
        {preferenceClassPriorities.map((classPriority) => (
          <AccordionItem
            value={`${classPriority.uuid}`}
            key={classPriority.uuid}
          >
            <PriorityHeader
              classPriority={classPriority}
              preference={preference}
              min={classDetails.minStudents}
              max={classDetails.maxStudents}
              subject={subject}
            />
            <AccordionContent>
              {classPriority.sessions.map(
                ({ day, duration, startTime }, index) => (
                  <TimetableSession
                    day={day}
                    start_time={startTime}
                    duration={duration}
                    eventTimezoneId={classDetails.timezoneId}
                    key={index}
                  />
                ),
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </li>
  )
}

function PriorityHeader({
  classPriority,
  min,
  max,
  preference,
  subject,
}: {
  preference: PreferenceEntity
  classPriority: ClassPriorityEntity
  subject: SubjectEntity
  min: number
  max: number
}) {
  const { tamsSchoolInput } = subject
  const { t } = useTranslation("preferences")
  const { name } = usePageSelector((state) =>
    selectPreferenceClass(state, { preference }),
  )

  const preferencePriority = usePageSelector((state: PageState) =>
    selectPreferencePriority(state, { classPriority }),
  )

  const dispatch = usePageDispatch()

  return (
    <div
      className="flex grow flex-row items-center gap-3"
      onPointerEnter={() => dispatch(priorityHovered(classPriority))}
      onPointerLeave={() => dispatch(priorityHovered(null))}
    >
      <AccordionTrigger className="gap-2">
        <div className="flex grow flex-col">
          <div className="flex grow flex-row items-center justify-between gap-1">
            <Label htmlFor={`priority-${classPriority.uuid}`}>
              {t("edit.class.preference.schedule.priority", {
                priority: classPriority.priority,
              })}
            </Label>
            {classPriority && tamsSchoolInput ? (
              <StatusTooltip
                priority={classPriority.priority.toString()}
                className={name}
                live={preferencePriority.live}
                allocated={preferencePriority.allocated}
                available={preferencePriority.available}
              />
            ) : null}
          </div>
          {classPriority && tamsSchoolInput ? (
            <span className="inline-flex grow items-center gap-1 text-sm [&>svg]:size-3">
              {t("class.preference.student_count", {
                allocated: preferencePriority.numStudents,
                count: max,
                min,
              })}
            </span>
          ) : null}
        </div>
      </AccordionTrigger>
    </div>
  )
}

function TimetableSession({
  day,
  start_time,
  duration,
  eventTimezoneId,
}: {
  day: number
  start_time: string
  duration: number
  eventTimezoneId: string
}) {
  const { calendarTimezoneId, startTemporalZonedDateTime } =
    usePageSelector(selectCalendar)
  const startTime = calculateEventStartCalendarTimezoneCoerced({
    startTime: start_time,
    day: day,
    calendarTimezoneId,
    eventTimezoneId,
    startTemporalZonedDateTime,
  })
  const { t } = useTranslation("preferences")

  return (
    <div>
      {t("timetable.schedule.fixed", {
        day: displayDay(day),
        start: displayPlainTime(startTime.toPlainTime()),
        end: displayPlainTime(
          startTime.add({ minutes: duration }).toPlainTime(),
        ),
      })}
    </div>
  )
}

function StatusTooltip({
  live,
  allocated,
  available,
  className,
  priority,
}: {
  live: boolean
  allocated: boolean
  available: boolean
  className: string
  priority: string
}) {
  const { t } = useTranslation("preferences")

  let status: "unavailable" | "live" | "allocated" | null = null

  if (allocated) {
    status = "allocated"
  }

  if (live) {
    status = "live"
  } else if (!available) {
    status = "unavailable"
  }

  if (!status) return null

  const variants: { [x: string]: React.ComponentProps<typeof Badge> } = {
    unavailable: { variant: "destructive" },
    live: { className: "bg-primary text-primary-foreground" },
    allocated: { variant: "secondary" },
  }

  return (
    <div>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            data-testid={`${status}-badge`}
            data-testlabel={`${className} Priority: ${priority}`}
            {...variants[status]}
          >
            <Info />
            {t(`edit.class.preference.${status}`)}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-48">
          {t(`edit.class.preference.${status}.description`)}
        </TooltipContent>
      </Tooltip>
    </div>
  )
}
